import React, { useRef, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useSelector } from "react-redux";
import useGroupItemContext from "../../../hooks/useGroupItemContext";

import { Container, Footer } from "./styles";
import { Row, Col, Input, Select, notification, Spin } from "antd";
import ButtonStandard from "../../../components/utils/button";
import {
  RadioButtonCustom,
  RadioGroupCustom,
} from "../../../components/drawers/supplier/styles";

import { save, saveForGroup } from "../../../services/groupItemService";

const validationSchema = Yup.object({
  name: Yup.string().trim().required(),
});

const GroupItemForm = () => {
  const formRef = useRef(null);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  const {
    translation,
    groupSelected: { id: groupId, name: groupName },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const {
    isDrawerVisible,
    data,
    closeDrawer,
    fetchData,
    fetchOptions,
    notifySaved,
  } = useGroupItemContext();

  function handleCloseDrawer() {
    resetFormValues();
    closeDrawer();
  }

  function resetFormValues() {
    const values = buildInitialValues(farmId);
    setForm(values);
    formRef.current.resetForm(values);
  }

  function buildInitialValues(resolvedFarmId) {
    return {
      id: null,
      groupId,
      farmId: resolvedFarmId,
      name: null,
      description: null,
      externalIntegrationCode: null,
      status: "Active",
    };
  }

  const [form, setForm] = useState(null);

  async function handleSubmitForm(values) {
    setIsLoadingRequest(true);
    try {
      const response = await save({
        groupId: groupId,
        farmId: farmId,
        id: values?.id,
        body: values,
      });
      notification.success({
        message: translation.groupItem.form.successMessage,
      });
      fetchData();
      fetchOptions();
      notifySaved(response?.data?.results || response?.data || null);
      handleCloseDrawer();
    } catch (error) {
      notification.error({
        message: "Erro",
        description: translation.groupItem.form.errorMessage,
      });
    } finally {
      setIsLoadingRequest(false);
    }
  }

  function handleDrawerVisible(visible) {
    if (visible) {
      if (data === null) {
        const values = buildInitialValues(farmId);
        setForm(values);
        formRef.current.resetForm(values);
      } else {
        setForm(data);
      }
    }
  }

  return (
    <Container
      title={
        data?.id == null
          ? translation.groupItem.form.titleCreateNew
          : translation.groupItem.form.titleEdit
      }
      zIndex={1013}
      width={700}
      onClose={handleCloseDrawer}
      maskClosable={false}
      visible={isDrawerVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Spin spinning={isLoadingRequest}>
        <Formik
          ref={formRef}
          enableReinitialize={true}
          initialValues={form}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
          render={({
            values,
            errors,
            submitCount,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="drawerForm">
                {errors.length > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}
                <Row type="flex" align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>
                        {translation.groupItem.form.groupFarmVisibility}
                      </label>
                    </Row>
                    <Row>
                      <RadioGroupCustom
                        value={
                          values?.groupId != null && values?.farmId != null
                            ? "farm"
                            : "group"
                        }
                        buttonStyle="solid"
                      >
                        <RadioButtonCustom
                          value="farm"
                          onChange={() => {
                            setFieldValue("groupId", groupId);
                            setFieldValue("farmId", farmId);
                          }}
                        >
                          {translation.supplier.form.radiogroup.farm}
                        </RadioButtonCustom>
                        <RadioButtonCustom
                          value="group"
                          onChange={() => {
                            setFieldValue("groupId", groupId);
                            setFieldValue("farmId", null);
                          }}
                        >
                          {translation.supplement.form.radiogroup.group}:{" "}
                          {groupName}
                        </RadioButtonCustom>
                      </RadioGroupCustom>
                    </Row>
                  </Col>
                </Row>
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.name && submitCount > 0 ? "error" : ""}
                  >
                    {translation.groupItem.form.name}*
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="name"
                    value={values?.name}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    onChange={(e) => setFieldValue("name", e.target.value)}
                  />
                </Row>
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.description && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.groupItem.form.description}*
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="description"
                    value={values?.description}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    onChange={(e) =>
                      setFieldValue("description", e.target.value)
                    }
                  />
                </Row>
                <Row type="flex" className="rowLabel">
                  <label>
                    {translation.groupItem.form.externalIntegrationCode}
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="externalIntegrationCode"
                    value={values?.externalIntegrationCode}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    onChange={(e) =>
                      setFieldValue("externalIntegrationCode", e.target.value)
                    }
                  />
                </Row>
              </div>
              <Footer>
                <Row type="flex">
                  <Col span={24} className="buttonsDiv">
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={closeDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default GroupItemForm;
